import { createError } from '@common/neo.tax';
import { usePopoverState, Typography, PopoverDisclosure, Stack, Button } from '@ui-components/neo.tax';
import { ReactComponent as SelectorIcon } from '@ui-components/neo.tax/dist/icons/selector.svg';
import identity from 'lodash/identity';
import { useMemo } from 'react';

import { FilterSortDataType } from '@neo.tax/shared/utils';

import { ConditionRow } from './ConditionRow';
import {
  StyledDropdownButton,
  StyledPopover,
  StyledAddConditionButton,
  StyledPopoverTitle,
  StyledPopoverTitleSemibold,
  StyledPlusIcon,
  StyledFilterIcon,
  StyledResetButton,
} from './filter-popover.styled';
import { FilterState } from './filter-popover.types';
import {
  ConditionRow as ConditionRowType,
  Conditions,
  DATE_OPERATORS,
  ENUM_OPERATORS,
  MONTH_YEAR_OPERATORS,
  NUMBER_OPERATORS,
  OperatorDefinition,
  OperatorName,
  STRING_OPERATORS,
} from './hooks';
import {
  createEnumValueInput,
  DateValueInput,
  MonthYearValueInput,
  NumberValueInput,
  StringValueInput,
  ValueInputType,
} from './value-inputs';

interface FilterPopoverProps<T extends string> extends FilterState<T> {
  /**
   * A description of what will be filtered, as a plural noun. Appears as "Only
   * show `<itemsDescription>`…".
   */
  itemsDescription: string;
}

const updateConditionRow = <ColumnName extends string>(
  conditions: Conditions<ColumnName>,
  id: string,
  update: (oldConditionRow: ConditionRowType<ColumnName>) => ConditionRowType<ColumnName>,
): Conditions<ColumnName> => ({
  booleanOperator: conditions.booleanOperator,
  conditionRows: conditions.conditionRows.map((conditionRow) => {
    if (conditionRow.id !== id) return conditionRow;
    return update(conditionRow);
  }),
});

export const FilterPopover = <ColumnName extends string>({
  columns,
  conditions,
  itemsDescription,
  resetConditions,
  setConditions,
}: FilterPopoverProps<ColumnName>): React.ReactElement => {
  const state = usePopoverState({ gutter: 6, placement: 'bottom-start' });
  const conditionCount = conditions.conditionRows.length;

  const columnsByName = useMemo(() => new Map(columns.map((col) => [col.name, col])), [columns]);

  // Look up column info by name
  const getColumnInfo = (
    columnName: ColumnName,
  ): {
    operators: readonly OperatorDefinition[];
    dataType:
      | FilterSortDataType.TEXT
      | FilterSortDataType.NUMBER
      | FilterSortDataType.DATE
      | FilterSortDataType.MONTH_YEAR_START
      | FilterSortDataType.MONTH_YEAR_END
      | FilterSortDataType.ENUM
      | FilterSortDataType.BOOLEAN;
    toHuman: (filterValue: string | number | null) => string | number | null;
    fromHuman: (humanValue: string | number | null) => string | number | null;
    ValueInput: ValueInputType;
  } => {
    const column = columnsByName.get(columnName);

    if (!column) {
      throw createError('NoColumnFound', `Filters tried to use a column called ${columnName}, but none was found.`);
    }

    let infoFromDataType: {
      operators: readonly OperatorDefinition[];
      ValueInput: ValueInputType;
    };

    switch (column.dataType) {
      case FilterSortDataType.TEXT:
        infoFromDataType = { operators: STRING_OPERATORS, ValueInput: StringValueInput };
        break;
      case FilterSortDataType.NUMBER:
        infoFromDataType = { operators: NUMBER_OPERATORS, ValueInput: NumberValueInput };
        break;
      case FilterSortDataType.DATE:
        infoFromDataType = { operators: DATE_OPERATORS, ValueInput: DateValueInput };
        break;
      case FilterSortDataType.MONTH_YEAR_START:
      case FilterSortDataType.MONTH_YEAR_END:
        infoFromDataType = { operators: MONTH_YEAR_OPERATORS, ValueInput: MonthYearValueInput };
        break;
      case FilterSortDataType.BOOLEAN:
        infoFromDataType = {
          operators: ENUM_OPERATORS,
          ValueInput: createEnumValueInput([
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]),
        };
        break;
      case FilterSortDataType.ENUM:
        infoFromDataType = {
          operators: ENUM_OPERATORS,
          ValueInput: createEnumValueInput(column.enum),
        };
        break;
      default:
        throw createError(
          'UnknownFilterSortDataType',
          `Filter column ${columnName} has unknown dataType ${column.dataType}.`,
        );
    }

    // Add info that's common to all data types
    return {
      ...infoFromDataType,
      dataType: column.dataType,
      toHuman: column.toHuman ?? identity,
      fromHuman: column.fromHuman ?? identity,
    };
  };

  const handleAddCondition = (): void => {
    setConditions((conditions) => ({
      booleanOperator: conditions.booleanOperator,
      conditionRows: [
        ...conditions.conditionRows,
        {
          id: Date.now().toString(),
          column: columns[0].name,
          dataType: columns[0].dataType,
          operator: getColumnInfo(columns[0].name).operators[0].name,
          value: null,
        },
      ],
    }));
  };

  const handleDeleteCondition = (id: string): void => {
    setConditions((conditions) => ({
      booleanOperator: conditions.booleanOperator,
      conditionRows: conditions.conditionRows.filter((oldCondition) => oldCondition.id !== id),
    }));
  };

  const handleChangeColumn = (id: string, newColumn: ColumnName): void => {
    setConditions((conditions) =>
      updateConditionRow<ColumnName>(conditions, id, (oldConditionRow) => {
        const newColumnInfo = getColumnInfo(newColumn);
        const validOperators = newColumnInfo.operators.map((o) => o.name);
        return {
          ...oldConditionRow,
          column: newColumn,
          dataType: newColumnInfo.dataType,
          // If the operator becomes invalid, reset to the first valid operator.
          operator: validOperators.includes(oldConditionRow.operator) ? oldConditionRow.operator : validOperators[0],
          // When column changes, reset the value to null.
          value: null,
        };
      }),
    );
  };

  const handleChangeOperator = (id: string, newOperator: OperatorName): void => {
    setConditions((conditions) =>
      updateConditionRow<ColumnName>(conditions, id, (oldConditionRow) => ({
        ...oldConditionRow,
        operator: newOperator,
      })),
    );
  };

  const handleChangeValue = (id: string, newValue: ConditionRowType<ColumnName>['value']): void => {
    setConditions((conditions) =>
      updateConditionRow<ColumnName>(conditions, id, (oldConditionRow) => ({ ...oldConditionRow, value: newValue })),
    );
  };

  const handleChangeBooleanOperator = (newValue: 'AND' | 'OR'): void => {
    setConditions((conditions) => ({
      booleanOperator: newValue,
      conditionRows: conditions.conditionRows,
    }));
  };

  return (
    <>
      <StyledDropdownButton
        as={PopoverDisclosure}
        state={state}
        active={conditionCount > 0}
        leadingIcon={<StyledFilterIcon />}
        trailingIcon={<SelectorIcon />}
        placeholder="No Filters"
        value={
          conditionCount > 0 ? (
            <>
              <Typography as="span" fontSize="sm" fontWeight="medium">
                {conditionCount}
              </Typography>{' '}
              {conditionCount === 1 ? 'Filter' : 'Filters'}
            </>
          ) : undefined
        }
      />
      <StyledPopover state={state} label="Filter popover">
        <Stack direction="column" gap={2.5}>
          <StyledPopoverTitle fontSize="sm" as="span" fontWeight="normal">
            {conditionCount === 0 ? (
              <>No filter conditions are applied</>
            ) : (
              <>
                Only show{' '}
                <StyledPopoverTitleSemibold fontSize="sm" as="span" fontWeight="medium">
                  {itemsDescription}
                </StyledPopoverTitleSemibold>
              </>
            )}
          </StyledPopoverTitle>
          {conditions.conditionRows.map((condition, index) => (
            <ConditionRow<ColumnName>
              key={condition.id}
              booleanOperator={conditions.booleanOperator}
              condition={condition}
              index={index}
              handleChangeBooleanOperator={handleChangeBooleanOperator}
              columns={columns}
              handleChangeColumn={handleChangeColumn}
              handleChangeOperator={handleChangeOperator}
              handleChangeValue={handleChangeValue}
              handleDeleteCondition={handleDeleteCondition}
              {...getColumnInfo(condition.column)}
            />
          ))}
          <Stack direction="row" justifyContent="space-between" gap={4}>
            <Button
              as={StyledAddConditionButton}
              onClick={handleAddCondition}
              rounded="regular"
              size="xs"
              startIcon={<StyledPlusIcon />}
              variant="primary"
            >
              {conditionCount > 0 ? 'Add another condition' : 'Add a condition'}
            </Button>

            {conditionCount > 0 && (
              <StyledResetButton onClick={resetConditions} size="xs" variant="white" rounded="regular">
                Reset filters
              </StyledResetButton>
            )}
          </Stack>
        </Stack>
      </StyledPopover>
    </>
  );
};
